import React from 'react';
import * as Sentry from '@sentry/react';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import { Error as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';

/**
 * Production-ready error boundary component
 * Catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging (in development only)
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Reported here, not gated on NODE_ENV: a boundary that catches stops React
    // from rethrowing, so Sentry's global handler never sees this error. This is
    // the only place it can be captured, in any environment.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } }
    });

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRefresh = () => {
    // Reset error state and refresh the page
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 2
            }}
          >
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            
            <Typography variant="h4" gutterBottom color="error.main">
              Something went wrong
            </Typography>
            
            <Typography variant="body1" paragraph color="text.secondary">
              We apologize for the inconvenience. An unexpected error has occurred.
            </Typography>
            
            {isDevelopment && this.state.error && (
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="h6" color="error.main" gutterBottom>
                  Development Error Details:
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: 'grey.100',
                    textAlign: 'left',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}
                >
                  <strong>Error:</strong> {this.state.error.toString()}
                  <br />
                  <br />
                  <strong>Component Stack:</strong>
                  <pre>{this.state.errorInfo.componentStack}</pre>
                </Paper>
              </Box>
            )}
            
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.handleRefresh}
              >
                Refresh Page
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </Box>
            
            <Typography variant="caption" display="block" sx={{ mt: 3 }} color="text.secondary">
              If this problem persists, please contact your system administrator.
            </Typography>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component wrapper for easier usage
 */
export const withErrorBoundary = (Component, fallback) => {
  return function WithErrorBoundaryComponent(props) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

/**
 * Hook for handling errors in functional components
 */
export const useErrorHandler = () => {
  return (error, errorInfo) => {
    // This would integrate with the error boundary
    // For now, we'll just throw the error to be caught by the boundary
    throw error;
  };
};

export default ErrorBoundary;